// Deterministic date arithmetic for the Chronology/Timeline computation
// layer (Round 12). No AI, no locale dependence — every duration the
// timeline shows is computed here in whole UTC days so it's click-traceable
// to two exact source dates.
//
// Why UTC-midnight differencing: records are stored as ISO strings anchored
// at UTC (see formatDate.ts). Subtracting raw epoch millis would let a
// time-of-day component leak a spurious ±1 day; flooring both ends to their
// UTC calendar midnight first makes daysBetween a pure calendar-day count.
//
// Pure functions, no React, no AI — unit-tested via node --test.

// Epoch millis at the UTC calendar midnight of `iso`'s day, or null when the
// input is missing/unparseable (callers render nothing rather than guess).
function utcMidnight(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

const MS_PER_DAY = 86_400_000;

// Whole calendar days from `fromISO` to `toISO`. SIGNED: positive when `to`
// is later than `from`, negative when earlier, 0 same day. null if either
// date is missing/unparseable. Correct across month/year boundaries because
// both ends collapse to UTC midnight before differencing.
export function daysBetween(
  fromISO: string | null | undefined,
  toISO: string | null | undefined,
): number | null {
  const a = utcMidnight(fromISO);
  const b = utcMidnight(toISO);
  if (a === null || b === null) return null;
  // Exact: both operands are integer multiples of MS_PER_DAY, so the
  // quotient is exact and Math.round only guards against FP representation.
  return Math.round((b - a) / MS_PER_DAY);
}

// Days elapsed from a past start date up to `nowISO` (defaults caller-side).
// Non-negative for a start in the past; negative if the start is in the
// future (caller decides how to phrase that). null when undeterminable.
export function daysElapsedSince(
  startISO: string | null | undefined,
  nowISO: string,
): number | null {
  return daysBetween(startISO, nowISO);
}

export interface ElapsedVsPromised {
  // Whole days from now to the promised date: >0 days remaining, <0 overdue,
  // 0 due today.
  days: number;
  overdue: boolean;
  dueToday: boolean;
}

// Compare a promised/deadline date against now. null when either date is
// missing/unparseable. The sign convention matches daysBetween(now, deadline):
// a future deadline is positive (days remaining), a passed one negative.
export function elapsedVsPromised(
  deadlineISO: string | null | undefined,
  nowISO: string,
): ElapsedVsPromised | null {
  const days = daysBetween(nowISO, deadlineISO);
  if (days === null) return null;
  return { days, overdue: days < 0, dueToday: days === 0 };
}

// Human phrasing for a signed day count, e.g. "12 days", "1 day". The caller
// supplies the surrounding sentence (over/under, elapsed/remaining); this
// only handles magnitude + pluralization so wording stays consistent.
export function formatDayCount(days: number): string {
  const n = Math.abs(days);
  return `${n} day${n === 1 ? "" : "s"}`;
}
